
import { SearchResult, SearchOptions } from './types';
import { getSearchHistory, addToSearchHistory, detectQueryType } from './utils';
import { searchClients } from './clientSearch';
import { searchOrders } from './orderSearch';
import { searchRentals } from './rentalSearch';
import { searchInventory } from './inventorySearch';
import { searchTasks } from './taskSearch';
import { searchStoreProducts } from './storeSearch';
import { searchWorkshops } from './workshopSearch';

class SearchService {
  private cache = new Map<string, { results: SearchResult[]; timestamp: number }>();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes

  async searchAll(query: string, options: SearchOptions = {}): Promise<SearchResult[]> {
    if (!query.trim()) return [];

    const cacheKey = `${query}-${JSON.stringify(options)}`;
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.results;
    }

    const queryType = detectQueryType(query);
    const { entityTypes = ['client', 'order', 'rental', 'inventory', 'task', 'store', 'workshop'], limit = 20 } = options;
    const results: SearchResult[] = [];

    try {
      // Интуитивная приоритизация поиска на основе типа запроса
      const searchPromises: Promise<SearchResult[]>[] = [];
      
      // Для ID запросов - приоритет заказов, аренд, затем остальное
      if (queryType === 'id') {
        if (entityTypes.includes('order')) {
          searchPromises.push(searchOrders(query, Math.ceil(limit * 0.3)));
        }
        if (entityTypes.includes('rental')) {
          searchPromises.push(searchRentals(query, Math.ceil(limit * 0.3)));
        }
        if (entityTypes.includes('client')) {
          searchPromises.push(searchClients(query, Math.ceil(limit * 0.2)));
        }
        if (entityTypes.includes('inventory')) {
          searchPromises.push(searchInventory(query, Math.ceil(limit * 0.1), options.userId));
        }
        if (entityTypes.includes('task')) {
          searchPromises.push(searchTasks(query, Math.ceil(limit * 0.05)));
        }
        if (entityTypes.includes('store')) {
          searchPromises.push(searchStoreProducts(query, Math.ceil(limit * 0.05), options.userId));
        }
        if (entityTypes.includes('workshop')) {
          searchPromises.push(searchWorkshops(query, options.userId || '', Math.ceil(limit * 0.05)));
        }
      }
      // Для email/phone запросов - приоритет клиентов
      else if (queryType === 'email' || queryType === 'phone') {
        if (entityTypes.includes('client')) {
          searchPromises.push(searchClients(query, Math.ceil(limit * 0.6)));
        }
        if (entityTypes.includes('order')) {
          searchPromises.push(searchOrders(query, Math.ceil(limit * 0.15)));
        }
        if (entityTypes.includes('rental')) {
          searchPromises.push(searchRentals(query, Math.ceil(limit * 0.15)));
        }
        if (entityTypes.includes('inventory')) {
          searchPromises.push(searchInventory(query, Math.ceil(limit * 0.05), options.userId));
        }
        if (entityTypes.includes('task')) {
          searchPromises.push(searchTasks(query, Math.ceil(limit * 0.03)));
        }
        if (entityTypes.includes('store')) {
          searchPromises.push(searchStoreProducts(query, Math.ceil(limit * 0.02), options.userId));
        }
        if (entityTypes.includes('workshop')) {
          searchPromises.push(searchWorkshops(query, options.userId || '', Math.ceil(limit * 0.02)));
        }
      }
      // Для текстовых запросов - равномерное распределение с акцентом на товары/инвентарь
      else {
        const perEntityLimit = Math.ceil(limit / entityTypes.length);
        
        if (entityTypes.includes('inventory')) {
          searchPromises.push(searchInventory(query, Math.ceil(perEntityLimit * 1.2), options.userId));
        }
        if (entityTypes.includes('store')) {
          searchPromises.push(searchStoreProducts(query, Math.ceil(perEntityLimit * 1.2), options.userId));
        }
        if (entityTypes.includes('client')) {
          searchPromises.push(searchClients(query, perEntityLimit));
        }
        if (entityTypes.includes('task')) {
          searchPromises.push(searchTasks(query, perEntityLimit));
        }
        if (entityTypes.includes('order')) {
          searchPromises.push(searchOrders(query, Math.ceil(perEntityLimit * 0.8)));
        }
        if (entityTypes.includes('rental')) {
          searchPromises.push(searchRentals(query, Math.ceil(perEntityLimit * 0.8)));
        }
        if (entityTypes.includes('workshop')) {
          searchPromises.push(searchWorkshops(query, options.userId || '', perEntityLimit));
        }
      }

      // Выполняем все поиски параллельно. Не падаем из-за одного источника.
      const settled = await Promise.allSettled(searchPromises);
      let fulfilledCount = 0;
      for (const s of settled) {
        if (s.status === 'fulfilled') {
          fulfilledCount += 1;
          const entityResults = s.value as SearchResult[];
          results.push(...entityResults);
        } else {
          // eslint-disable-next-line no-console
          console.error('[GlobalSearch] source rejected:', s.reason);
        }
      }
      if (fulfilledCount === 0) {
        // Если не удалось получить ни один источник — логируем и возвращаем пусто, не роняем UI
        // eslint-disable-next-line no-console
        console.error('[GlobalSearch] all sources failed, returning empty results');
        return [];
      }

      // Интеллектуальная сортировка: сначала по релевантности, затем по типу
      const sortedResults = results
        .sort((a, b) => {
          // Приоритет по релевантности
          if (b.relevance !== a.relevance) {
            return b.relevance - a.relevance;
          }
          
          // При равной релевантности - приоритет по типу запроса
          const typeOrder = this.getTypeOrderByQuery(queryType);
          const aIndex = typeOrder.indexOf(a.type);
          const bIndex = typeOrder.indexOf(b.type);
          
          return aIndex - bIndex;
        })
        .slice(0, limit);

      // Cache results
      this.cache.set(cacheKey, { results: sortedResults, timestamp: Date.now() });

      return sortedResults;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Search failed:', error);
      return [];
    }
  }

  getSearchHistory(): string[] {
    return getSearchHistory();
  }

  addToSearchHistory(query: string): void {
    addToSearchHistory(query);
  }

  clearCache(): void {
    this.cache.clear();
  }

  // Определение порядка типов сущностей в зависимости от типа запроса
  private getTypeOrderByQuery(queryType: string): string[] {
    switch (queryType) {
      case 'id':
        return ['order', 'rental', 'client', 'inventory', 'task', 'store'];
      case 'email':
      case 'phone':
        return ['client', 'order', 'rental', 'inventory', 'task', 'store'];
      case 'text':
      default:
        return ['inventory', 'store', 'client', 'task', 'order', 'rental'];
    }
  }
}

export const searchService = new SearchService();
